import type { Animation, LoopMode } from '../domain/schemas';
import { Observable } from '../lib/store';
import { frameAtTime } from './timeline';

/** Timing of the animation the clock is playing; null plays open-ended (Phase 0 shapes only). */
export interface ClockTiming {
  fps: number;
  durationFrames: number;
  loop: LoopMode;
}

export interface ClockState {
  playing: boolean;
  /** Playback position (seconds since frame 0) at `originMs`. */
  originSec: number;
  /** Monotonic timestamp (ms) at which `originSec` was taken. */
  originMs: number;
  /** Playback speed multiplier (0.25 – 4). */
  speed: number;
  timing: ClockTiming | null;
  /** Identity of the loaded animation (its id), or null for open-ended playback. */
  animationKey: string | null;
  /** Bumped on every seek/reset/timing change so paused views redraw. */
  revision: number;
}

export interface ClockSample {
  /** Unwrapped playback seconds (also drives the Phase 0 shapes). */
  seconds: number;
  /** Frame to display (wrapped by the loop mode). */
  frame: number;
  /** A "once" animation has played past its last frame. */
  ended: boolean;
}

/**
 * What the renderer needs from a clock (the ownership boundary between clocks). The
 * standalone animation clock (`PlaybackClock`, Phase 3B) and the audio-visual session clock
 * (`SessionClock`, Phase 4A) both provide it; a renderer follows exactly one of them.
 */
export interface FrameClock {
  get(): { playing: boolean };
  subscribe(listener: () => void): () => void;
  /** Playback seconds (drives Phase 0 shapes and reactions). */
  seconds(): number;
  sample(): ClockSample;
  play(): void;
  pause(): void;
  reset(): void;
  /** Called by the renderer when `sample().ended` is reached. */
  finish(): void;
}

/** Frame rate used for the frame counter when no animation is selected. */
export const OPEN_ENDED_FPS = 30;
export const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 1.5, 2, 4] as const;

export const timingOf = (a: Pick<Animation, 'fps' | 'durationFrames' | 'loop'> | null | undefined): ClockTiming | null =>
  a ? { fps: a.fps, durationFrames: a.durationFrames, loop: a.loop } : null;

const sameTiming = (a: ClockTiming | null, b: ClockTiming | null) =>
  a === b || (!!a && !!b && a.fps === b.fps && a.durationFrames === b.durationFrames && a.loop === b.loop);

/**
 * The single animation clock (Phase 3B). Every consumer — the Pixi renderer, the timeline
 * playhead, Phase 0 shapes, prompts — reads time from here; nothing else keeps time.
 *
 * Time is computed, never accumulated: `seconds = originSec + (now − originMs) × speed`
 * from a monotonic clock (`performance.now()`), and the displayed frame is derived from
 * that time (`frameAtTime`). So playback has no drift, and dropped or late browser
 * frames skip animation frames instead of slowing it down. Pausing, seeking, restarting,
 * changing speed or timing re-anchors the origin.
 */
export class PlaybackClock extends Observable<ClockState> {
  constructor(private readonly now: () => number = () => performance.now()) {
    super({ playing: false, originSec: 0, originMs: 0, speed: 1, timing: null, animationKey: null, revision: 0 });
  }

  /** Unwrapped playback seconds at `at` (default: now). */
  seconds(at = this.now()): number {
    const s = this.get();
    return s.playing ? s.originSec + ((at - s.originMs) / 1000) * s.speed : s.originSec;
  }

  sample(at = this.now()): ClockSample {
    const seconds = this.seconds(at);
    const timing = this.get().timing;
    if (!timing) return { seconds, frame: Math.floor(seconds * OPEN_ENDED_FPS + 1e-6), ended: false };
    const { frame, ended } = frameAtTime(seconds, timing);
    return { seconds, frame, ended };
  }

  /** Current frame (wrapped). */
  frame(): number {
    return this.sample().frame;
  }

  play(): void {
    const s = this.get();
    if (s.playing) return;
    // Playing a finished one-shot animation starts it again from the beginning.
    const restart = s.timing?.loop === 'once' && this.sample().frame >= s.timing.durationFrames - 1;
    this.set({ ...s, playing: true, originSec: restart ? 0 : this.seconds(), originMs: this.now(), revision: s.revision + 1 });
  }

  pause(): void {
    const s = this.get();
    if (!s.playing) return;
    this.set({ ...s, playing: false, originSec: this.loopedSeconds(), originMs: this.now(), revision: s.revision + 1 });
  }

  toggle(): void {
    if (this.get().playing) this.pause();
    else this.play();
  }

  /** Back to frame 0 (keeps playing if it was). */
  reset(): void {
    const s = this.get();
    this.set({ ...s, originSec: 0, originMs: this.now(), revision: s.revision + 1 });
  }

  /** Back to frame 0 and play. */
  restart(): void {
    const s = this.get();
    this.set({ ...s, playing: true, originSec: 0, originMs: this.now(), revision: s.revision + 1 });
  }

  /** Moves the playhead to a frame (clamped to the animation); keeps the play state. */
  seek(frame: number): void {
    const s = this.get();
    const fps = s.timing?.fps ?? OPEN_ENDED_FPS;
    const max = s.timing ? s.timing.durationFrames - 1 : Number.MAX_SAFE_INTEGER;
    const f = Math.min(max, Math.max(0, Math.round(Number.isFinite(frame) ? frame : 0)));
    this.set({ ...s, originSec: f / fps, originMs: this.now(), revision: s.revision + 1 });
  }

  /** Pauses and moves by whole frames. */
  step(delta: number): void {
    const current = this.frame();
    this.pause();
    this.seek(current + delta);
  }

  setSpeed(speed: number): void {
    const s = this.get();
    const next = Math.min(4, Math.max(0.25, Number.isFinite(speed) ? speed : 1));
    if (next === s.speed) return;
    this.set({ ...s, speed: next, originSec: this.seconds(), originMs: this.now(), revision: s.revision + 1 });
  }

  /**
   * Loads the animation to play, identified by `key` (its id; null = no animation, an
   * open-ended clock for the Phase 0 shapes). The contract is keyed by identity, never
   * by comparing timing values:
   *  - a **different** key always starts at frame 0 — even when its fps, duration and
   *    loop mode equal the previous animation's;
   *  - the **same** key with changed timing (an edit of the current animation) keeps the
   *    playhead's position *in time* (so keyframes stay under it when the fps changes),
   *    rounded to the nearest frame and clamped to the new last frame;
   *  - the same key with the same timing is a no-op, so re-renders never reset playback.
   * In every case the play state is kept: a playing clock keeps playing from the new
   * position, a paused one stays paused there.
   */
  load(key: string | null, timing: ClockTiming | null): void {
    const s = this.get();
    if (key === s.animationKey && sameTiming(s.timing, timing)) return;
    let seconds = 0;
    if (key === s.animationKey) {
      const position = this.loopedSeconds();
      if (timing) {
        const frame = Math.min(timing.durationFrames - 1, Math.max(0, Math.round(position * timing.fps)));
        seconds = frame / timing.fps;
      } else seconds = position;
    }
    this.set({ ...s, timing, animationKey: key, originSec: seconds, originMs: this.now(), revision: s.revision + 1 });
  }

  /** Stops a finished one-shot animation on its last frame. */
  finish(): void {
    const s = this.get();
    if (!s.timing) return;
    this.set({ ...s, playing: false, originSec: (s.timing.durationFrames - 1) / s.timing.fps, originMs: this.now(), revision: s.revision + 1 });
  }

  /** Seconds folded into the animation, so pausing a looping animation keeps the frame. */
  private loopedSeconds(): number {
    const s = this.get();
    if (!s.timing) return this.seconds();
    return this.sample().frame / s.timing.fps;
  }
}
